import { isPlainObject, cloneDeep, merge, isArray } from 'lodash-es';
import { InjectionToken, Directive, Inject, Input, HostListener, NgModule } from '@angular/core';
import { createAction, props, createFeatureSelector, createSelector, select, Store, createReducer, on, StoreModule } from '@ngrx/store';
import '@angular/forms';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import * as ɵngcc0 from '@angular/core';
import * as ɵngcc1 from '@ngrx/store';
const INIT_ACTION = '@ngrx/store/init';
const INIT_ACTION_EFFECTS = '@ngrx/effects/init';
const UPDATE_ACTION = '@ngrx/store/update-reducers';

const dateMatcher = /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/;
/**
 * @internal Restores the resolved state from a storage location
 * @param options the configurable options
 * @returns the restored state from the provided storage
 */
const rehydrateState = ({ storage, storageKeySerializer, features, storageError, version: currentVersion, }) => {
    if (currentVersion) {
        try {
            const key = storageKeySerializer('version');
            const version = +storage.getItem(key);
            if (version < currentVersion) {
                return null;
            }
        }
        catch (e) {
            if (storageError) {
                storageError(e);
            }
            else {
                throw e;
            }
        }
    }
    const state = features.reduce((acc, curr) => {
        const { storageKeySerializerForFeature, stateKey, deserialize, storageForFeature } = curr;
        const key = storageKeySerializerForFeature
            ? storageKeySerializerForFeature(stateKey)
            : storageKeySerializer(stateKey);
        try {
            const featureState = storageForFeature ? storageForFeature.getItem(key) : storage.getItem(key);
            return featureState
                ? Object.assign(Object.assign({}, acc), {
                    [stateKey]: deserialize
                        ? deserialize(featureState)
                        : JSON.parse(featureState, (_, value) => dateMatcher.test(String(value)) ? new Date(value) : value),
                }) : acc;
        }
        catch (e) {
            if (storageError) {
                storageError(e);
            }
            else {
                throw e;
            }
        }
    }, Object());
    return Object.keys(state).length ? state : null;
};

/**
 * @internal Blacklisting
 * @returns the filtered featureState
 */
const excludeKeysFromState = (featureState, excludeKeys) => {
    if (!excludeKeys) {
        return featureState;
    }
    const keyPairs = excludeKeys.map((key) => ({
        leftKey: key.split('.')[0],
        rightKey: key.split('.')[1],
    }));
    for (const key in featureState) {
        if (featureState.hasOwnProperty(key)) {
            const keyPair = keyPairs.find((pair) => pair.leftKey === key);
            const leftKey = keyPair === null || keyPair === void 0 ? void 0 : keyPair.leftKey;
            const rightKey = keyPair === null || keyPair === void 0 ? void 0 : keyPair.rightKey;
            switch (typeof featureState[key]) {
                case 'object': {
                    if (leftKey && !featureState[key]) {
                        delete featureState[key];
                    }
                    else if (leftKey && rightKey) {
                        excludeKeysFromState(featureState[key], [...excludeKeys, rightKey]);
                    }
                    else if (leftKey) {
                        delete featureState[key];
                    }
                    else {
                        excludeKeysFromState(featureState[key], excludeKeys);
                    }
                    break;
                }
                default: {
                    if (leftKey) {
                        delete featureState[key];
                    }
                }
            }
        }
    }
    return featureState;
};
/**
 * @internal Remove empty objects from featureState
 * @returns the cleaned featureState
 */
const cleanState = (featureState) => {
    for (const key in featureState) {
        if (!isPlainObject(featureState[key])) {
            continue;
        }
        cleanState(featureState[key]);
        if (!Object.keys(featureState[key]).length) {
            delete featureState[key];
        }
    }
    return featureState;
};
/**
 * @internal Sync state with storage
 * @param state the next state
 * @param options the configurable options
 * @returns the next state
 */
const stateSync = (state, { features, storage, storageKeySerializer, storageError, version: currentVersion }) => {
    if (currentVersion) {
        try {
            const key = storageKeySerializer('version');
            storage.setItem(key, String(currentVersion));
        }
        catch (e) {
            if (storageError) {
                storageError(e);
            }
            else {
                throw e;
            }
        }
    }
    features
        .filter(({ stateKey }) => state[stateKey] !== undefined)
        .filter(({ stateKey, shouldSync }) => (shouldSync ? shouldSync(state[stateKey], state) : true))
        .forEach(({ stateKey, excludeKeys, storageKeySerializerForFeature, serialize, storageForFeature }) => {
        const featureState = cloneDeep(state[stateKey]);
        const cleanedState = cleanState(excludeKeysFromState(featureState, excludeKeys));
        if (isPlainObject(cleanedState) && !Object.keys(cleanedState).length) {
            return;
        }
        const key = storageKeySerializerForFeature
            ? storageKeySerializerForFeature(stateKey)
            : storageKeySerializer(stateKey);
        const value = serialize ? serialize(cleanedState) : JSON.stringify(cleanedState);
        try {
            if (storageForFeature) {
                storageForFeature.setItem(key, value);
            }
            else {
                storage.setItem(key, value);
            }
        }
        catch (e) {
            if (storageError) {
                storageError(e);
            }
            else {
                throw e;
            }
        }
    });
    return state;
};

/**
 * The StorageSync Meta Reducer for @ngrx/store.
 *
 * @param options The configuration for the meta reducer
 *
 * Check out github for more information.
 * @see https://github.com/larscom/ngrx-store-storagesync
 *
 * @returns the meta reducer function
 */
const storageSync = (options) => (reducer) => {
    const config = Object.assign({ rehydrate: true, storageKeySerializer: (key) => key, rehydrateStateMerger: (nextState, rehydratedState) => merge({}, nextState, rehydratedState) }, options);
    const { rehydrate, rehydrateStateMerger } = config;
    const revivedState = rehydrate ? rehydrateState(config) : null;
    return (state, action) => {
        const nextState = action.type === INIT_ACTION ? reducer(state, action) : Object.assign({}, state);
        const shouldMerge = revivedState && [INIT_ACTION, UPDATE_ACTION].includes(action.type);
        const mergedState = reducer(shouldMerge ? rehydrateStateMerger(nextState, revivedState) : nextState, action);
        return [INIT_ACTION, INIT_ACTION_EFFECTS].includes(action.type) ? mergedState : stateSync(mergedState, config);
    };
};

const FORM_SYNC_STORE_KEY = 'formSync';

const FORM_SYNC_CONFIG = new InjectionToken('FORM_SYNC_CONFIG', {
    factory: () => ({ syncOnSubmit: false, syncRawValue: false, syncValidOnly: false })
});
const FORM_SYNC_REDUCER = new InjectionToken('FORM_SYNC_REDUCER');

const setForm = createAction('@larscom/form-sync/set', props());
const patchForm = createAction('@larscom/form-sync/patch', props());
const resetForm = createAction('@larscom/form-sync/reset', props());
const deleteForm = createAction('@larscom/form-sync/delete', props());

const getFormSyncState = createFeatureSelector(FORM_SYNC_STORE_KEY);
const ɵ0$1 = (state, { id }) => state[id];
const getFormSyncValue = createSelector(getFormSyncState, ɵ0$1);

class FormGroupDirective {
    constructor(config, store) {
        this.config = config;
        this.store = store;
        this.formGroupSync = true;
        this.subscriptions = new Subscription();
    }
    ngOnInit() {
        if (!this.formGroupId) {
            return;
        }
        const { syncOnSubmit, syncValidOnly, syncRawValue } = this.config;
        this.subscriptions.add(this.formGroup.valueChanges
            .pipe(filter(() => this.formGroupSync), filter(() => !(syncValidOnly && !this.formGroup.valid)), filter(() => !syncOnSubmit))
            .subscribe(() => this.dispatch(syncRawValue)));
        this.subscriptions.add(this.store
            .pipe(filter(() => this.formGroupSync), select(getFormSyncValue, { id: this.formGroupId }), filter(value => value != null))
            .subscribe(value => this.formGroup.patchValue(value, { emitEvent: false })));
    }
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }
    onSubmit() {
        if (!this.formGroupId || !this.formGroupSync) {
            return;
        }
        const { syncOnSubmit, syncValidOnly, syncRawValue } = this.config;
        if (!syncOnSubmit) {
            return;
        }
        if (syncValidOnly && !this.formGroup.valid) {
            return;
        }
        this.dispatch(syncRawValue);
    }
    dispatch(syncRawValue) {
        const value = syncRawValue ? this.formGroup.getRawValue() : this.formGroup.value;
        this.store.dispatch(patchForm({ id: this.formGroupId, value }));
    }
}
FormGroupDirective.ɵfac = function FormGroupDirective_Factory(t) { return new (t || FormGroupDirective)(ɵngcc0.ɵɵdirectiveInject(FORM_SYNC_CONFIG), ɵngcc0.ɵɵdirectiveInject(ɵngcc1.Store)); };
FormGroupDirective.ɵdir = /*@__PURE__*/ ɵngcc0.ɵɵdefineDirective({ type: FormGroupDirective, selectors: [["", "formGroup", ""]], hostBindings: function FormGroupDirective_HostBindings(rf, ctx) { if (rf & 1) {
        ɵngcc0.ɵɵlistener("submit", function FormGroupDirective_submit_HostBindingHandler() { return ctx.onSubmit(); });
    } }, inputs: { formGroupSync: "formGroupSync", formGroup: "formGroup", formGroupId: "formGroupId" } });
/** @nocollapse */
FormGroupDirective.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: [FORM_SYNC_CONFIG,] }] },
    { type: Store }
];
FormGroupDirective.propDecorators = {
    formGroup: [{ type: Input }],
    formGroupId: [{ type: Input }],
    formGroupSync: [{ type: Input }],
    onSubmit: [{ type: HostListener, args: ['submit',] }]
};
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(FormGroupDirective, [{
        type: Directive,
        args: [{
                selector: '[formGroup]'
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Inject,
                args: [FORM_SYNC_CONFIG]
            }] }, { type: ɵngcc1.Store }]; }, { formGroupSync: [{
            type: Input
        }], onSubmit: [{
            type: HostListener,
            args: ['submit']
        }], formGroup: [{
            type: Input
        }], formGroupId: [{
            type: Input
        }] }); })();

const ɵ0 = (state, { id, value }) => (Object.assign(Object.assign({}, state), { [id]: value })), ɵ1 = (state, { id, value }) => merge({}, Object.assign({}, state), { [id]: value }), ɵ2 = (state, { id }) => {
    const reset = (slice) => {
        for (const property in slice) {
            if (!slice.hasOwnProperty(property)) {
                continue;
            }
            if (isPlainObject(slice[property])) {
                reset(slice[property]);
            }
            else if (isArray(slice[property])) {
                Array(slice[property]).forEach(p => reset(p));
            }
            else {
                slice[property] = null;
            }
        }
        return slice;
    };
    const value = reset(cloneDeep(state[id]));
    return Object.assign(Object.assign({}, state), { [id]: value });
}, ɵ3 = (state, { id }) => (Object.assign(Object.assign({}, state), { [id]: undefined }));
/**
 * The form sync reducer for @ngrx/store.
 */
// tslint:disable: no-shadowed-variable
const formSyncReducer = createReducer(Object(), on(setForm, ɵ0), on(patchForm, ɵ1), on(resetForm, ɵ2), on(deleteForm, ɵ3));

class FormSyncModule {
    /**
     * Import 'FormSyncModule.forRoot()' only once.
     * Use 'FormSyncModule' for additional imports
     * inside feature modules.
     */
    static forRoot() {
        return {
            ngModule: FormSyncModule,
            providers: [
                {
                    provide: FORM_SYNC_REDUCER,
                    useValue: formSyncReducer
                }
            ]
        };
    }
}
FormSyncModule.ɵfac = function FormSyncModule_Factory(t) { return new (t || FormSyncModule)(); };
FormSyncModule.ɵmod = /*@__PURE__*/ ɵngcc0.ɵɵdefineNgModule({ type: FormSyncModule });
FormSyncModule.ɵinj = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjector({ imports: [[StoreModule.forFeature(FORM_SYNC_STORE_KEY, FORM_SYNC_REDUCER)]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(FormSyncModule, [{
        type: NgModule,
        args: [{
                declarations: [FormGroupDirective],
                imports: [StoreModule.forFeature(FORM_SYNC_STORE_KEY, FORM_SYNC_REDUCER)],
                exports: [FormGroupDirective]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(FormSyncModule, { declarations: [FormGroupDirective], imports: [ɵngcc1.StoreFeatureModule], exports: [FormGroupDirective] }); })();

/**
 * Generated bundle index. Do not edit.
 */

export { FORM_SYNC_CONFIG, FORM_SYNC_REDUCER, FORM_SYNC_STORE_KEY, FormGroupDirective, FormSyncModule, deleteForm, formSyncReducer, getFormSyncValue, patchForm, resetForm, setForm, storageSync, getFormSyncState as ɵa };

